<template>
  <div>
    <div class="gs-checkout-v2">
      <section class="gs-checkout-v2__summary">
        <div class="gs-checkout-v2__summary-copy">
          <button type="button" class="gs-checkout-v2__back" @click="goToFunnels">
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M12.75 4.5 7.25 10l5.5 5.5" />
            </svg>
            {{ studioReturn ? 'Back to Funnel Studio' : 'All product funnels' }}
          </button>
          <p class="gs-checkout-v2__eyebrow">Checkout links</p>
          <h1>{{ selectedProduct ? selectedProduct.name : 'Checkout workspace' }}</h1>
          <p>Build, edit, and share every checkout experience for this product from one focused workspace.</p>
        </div>
        <div v-if="selectedProduct && !loadError" class="gs-checkout-v2__summary-side">
          <div class="gs-checkout-v2__stats" aria-label="Checkout summary">
            <span><strong>{{ checkoutLinks.length }}</strong> total</span>
            <span><strong>{{ productPricePointCount }}</strong> price points</span>
          </div>
          <div class="gs-checkout-v2__header-actions">
            <gd-button variant="primary" @click="openNewCheckoutPage">
              <span class="gs-checkout-v2__plus" aria-hidden="true">+</span>
              Create checkout
            </gd-button>
          </div>
        </div>
      </section>

      <v-wait for="products">
        <section v-if="loadError" class="gs-checkout-v2__state gs-checkout-v2__state--error" role="alert">
          <div class="gs-checkout-v2__state-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 8v5m0 3h.01M10.3 3.7 2.8 17a2 2 0 0 0 1.74 3h14.92a2 2 0 0 0 1.74-3L13.7 3.7a2 2 0 0 0-3.4 0Z" />
            </svg>
          </div>
          <p class="gs-checkout-v2__state-eyebrow">Checkout workspace unavailable</p>
          <h2>{{ loadError.title }}</h2>
          <p>{{ loadError.message }}</p>
          <div class="gs-checkout-v2__state-actions">
            <gd-button variant="primary" @click="retryCheckoutLinks">Try again</gd-button>
            <gd-button variant="outline" @click="goToFunnels">{{ studioReturn ? 'Back to Funnel Studio' : 'Back to funnels' }}</gd-button>
          </div>
          <small>No checkout settings were changed.</small>
        </section>

        <template v-else>
          <section class="gs-checkout-v2__toolbar" aria-label="Choose funnel and product">
            <div>
              <p class="gs-checkout-v2__toolbar-label">Viewing checkout links for</p>
              <navigation-header :funnels="funnels" :funnel="funnel" :selectedFunnelId="selectedFunnelId"
                :selectedProductId="selectedProductId" :openCheckoutPagesModal="openNewCheckoutPage"
                :showCreate="false"
              />
            </div>
            <p class="gs-checkout-v2__toolbar-help">Switch products without leaving your checkout workspace.</p>
          </section>

          <div v-if="checkoutLinks.length" class="gs-checkout-v2__section-heading">
            <div>
              <p>Checkout experiences</p>
              <h2>Ready to share</h2>
            </div>
            <span>{{ checkoutLinks.length }} {{ checkoutLinks.length === 1 ? 'checkout' : 'checkouts' }}</span>
          </div>

          <div class="gs-checkout-v2__grid">
            <article
              v-for="checkoutLink in checkoutLinks"
              :key="checkoutLink.id"
              class="gs-checkout-v2__card"
            >
              <div class="gs-checkout-v2__card-main">
                <div class="gs-checkout-v2__card-topline">
                  <div class="gs-checkout-v2__card-icon" aria-hidden="true">
                    <shopping-cart-icon class="w-5 h-5 fill-current" />
                  </div>
                  <span>{{ checkoutExperienceLabel(checkoutLink) }}</span>
                  <div class="gs-checkout-v2__card-menu">
                    <button type="button" class="gs-checkout-v2__menu-trigger" aria-label="More checkout actions" @click.stop="toggleCheckoutMenu(checkoutLink.id)">⋯</button>
                    <div v-if="openCheckoutMenuId === checkoutLink.id" class="gs-checkout-v2__menu" role="menu">
                      <button type="button" role="menuitem" @click="cloneCheckout(checkoutLink)">Clone</button>
                      <button type="button" role="menuitem" class="is-danger" @click="openDeleteCheckoutModal(checkoutLink)">Delete</button>
                    </div>
                  </div>
                </div>
                <div class="gs-checkout-v2__card-copy">
                  <p class="gs-checkout-v2__card-kicker">Checkout name</p>
                  <h2>{{ checkoutDisplayName(checkoutLink) }}</h2>
                  <p>{{ checkoutDescription(checkoutLink) }}</p>
                  <dl class="gs-checkout-v2__meta">
                    <div>
                      <dt>Template</dt>
                      <dd>{{ checkoutTemplateLabel(checkoutLink) }}</dd>
                    </div>
                    <div>
                      <dt>Price points</dt>
                      <dd>{{ checkoutPricePointSummary(checkoutLink) }}</dd>
                    </div>
                    <div>
                      <dt>Checkout URL</dt>
                      <dd>{{ checkoutUrlSummary(checkoutLink) }}</dd>
                    </div>
                    <div>
                      <dt>Updated</dt>
                      <dd>{{ checkoutUpdatedLabel(checkoutLink) }}</dd>
                    </div>
                  </dl>
                </div>
              </div>
              <div class="gs-checkout-v2__card-actions">
                <gd-button variant="outline" size="auto" @click="openCheckoutPageLink(checkoutLink.id)">
                  Get link
                </gd-button>
                <gd-button variant="primary" size="auto" @click="openCheckoutBuilder(checkoutLink)">
                  <span class="gs-checkout-v2__button-icon" aria-hidden="true">
                    <pencil-icon class="w-4 h-4 fill-current" />
                  </span>
                  Edit checkout
                </gd-button>
              </div>
            </article>

            <div v-if="!checkoutLinks.length" class="gs-checkout-v2__state gs-checkout-v2__empty">
              <div class="gs-checkout-v2__state-icon gs-checkout-v2__state-icon--empty" aria-hidden="true">
                <shopping-cart-icon class="w-7 h-7 fill-current" />
              </div>
              <p class="gs-checkout-v2__state-eyebrow">Start selling</p>
              <h2>Create your first checkout</h2>
              <p>Choose a checkout page or widget, then customize the customer experience for this product.</p>
              <gd-button variant="primary" @click="openNewCheckoutPage">
                <span class="gs-checkout-v2__plus" aria-hidden="true">+</span>
                Create checkout
              </gd-button>
            </div>
          </div>
        </template>
      </v-wait>

    </div>

    <gs-modal
      v-model="showCheckoutPagesModal"
      class="gs-checkout-builder-modal"
      :title="checkoutModalTitle"
      size="xl"
      :close-on-backdrop="false"
    >
      <div class="gs-checkout-wizard">
        <steper-header :selectedCheckoutFlow="selectedCheckoutFlow" :currentTab="currentTab" :errorTabs="errorTabs"
          :tabs="tabs" :moveBackOnWizard="moveBackOnWizard" :moveNextOnWizard="moveNextOnWizard"
          :selectTab="selectCheckoutWizardTab" :closeWizard="closeCheckoutWizard"
          :allowAllTabs="Boolean(model.isEdit)"
          :isSaving="isSavingCheckoutStep" />
        <main class="gs-checkout-wizard__content">
          <v-wait for="create_coupon">
          </v-wait>
          <div class="gs-checkout-wizard__content-inner" :class="{ 'is-links-step': currentTab === 'LinksStep' }">
            <header class="gs-checkout-wizard__step-header">
              <div>
                <p>Step {{ currentCheckoutTabIndex + 1 }} of {{ tabs.length }}</p>
                <h2>{{ activeCheckoutTab ? activeCheckoutTab.label : 'Checkout setup' }}</h2>
                <span>{{ activeCheckoutTab ? activeCheckoutTab.description : 'Configure this checkout experience.' }}</span>
              </div>
              <span class="gs-checkout-wizard__save-note">
                {{ currentTab === 'LinksStep' ? 'Checkout ready to share' : 'Changes save when you continue' }}
              </span>
            </header>
            <section class="gs-checkout-wizard__step-surface">
              <component v-if="currentTab" :is="currentTab" :ref="currentTab" :model="model" :product="selectedProduct"
                :selectCheckoutStepType="selectedCheckoutStepType" :selectedCheckoutStepType="selectedCheckoutStepType"
                :selectedMultiStepTypeProp="selectedMultiStepType"
                :selectedCheckoutTypeFreePaid="selectedCheckoutTypeFreePaid" :isMultiStepRedirectProp="isMultiStepRedirect"
                :pricePoints="selectedProductPricePoints" :prductDetails="selectedProduct"
                :pricepointTabs="projectionPricePointsTabs" :projectionsPricePointsTab="projectionPricePointsTab"
                :handleProjectionPricePointTabChange="handleProjectionPricePointsTabChange"
                :pricepointsForProjections="pricepointsForProjections" :selectPricePoint="selectedProductPricePoints"
                :disableBrandingBarProp="disableBrandingBar" :selectedMultiStepType="selectedMultiStepType"
                :checkoutLinks="checkoutLinks" @updateModel="model = $event"
                @updatePricePoints="selectedProductPricePoints = $event"
                @updateSelCheckoutStepType="selectedCheckoutStepType = $event"
                @updateselectedMultiStepType="selectedMultiStepType = $event"
                @updateselectedCheckoutTypeFreePaid="selectedCheckoutTypeFreePaid = $event"
                @updatePrductDetailsProp="selectedProduct = $event" @updateCheckoutProp="checkoutLinks = $event"
                @disableBrandingBarChange="disableBrandingBar = $event"
                @updateIsMultiStepRedirect="isMultiStepRedirect = $event"
                @updateMultiStepRedirectOptions="model.multi_step_redirect_options = $event"
                @on-validate="stepValidationSuccessful(currentTab, $event)" />
            </section>
          </div>
        </main>
      </div>
    </gs-modal>
    <modern-checkout-link-modal v-model="showCheckoutLinkModal" :checkout-link="multiPricePointLink"
      :checkout_link="checkout_link" :checkout_embed="checkout_embed" />
    <gs-modal v-model="showDeleteCheckoutModal" title="Delete checkout?" size="sm">
      <div class="gs-checkout-v2__delete-modal">
        <div class="gs-checkout-v2__delete-modal-icon" aria-hidden="true">!</div>
        <h3>{{ checkoutLinkToDelete ? checkoutDisplayName(checkoutLinkToDelete) : 'This checkout' }}</h3>
        <p>This permanently removes the checkout experience. Any checkout links already shared for it will stop working immediately.</p>
        <div class="gs-checkout-v2__delete-modal-actions">
          <gd-button variant="outline" @click="showDeleteCheckoutModal = false">Cancel</gd-button>
          <gd-button variant="primary" class="gs-checkout-v2__delete-confirm" @click="deleteCheckout">Delete checkout</gd-button>
        </div>
      </div>
    </gs-modal>
    <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
      <template v-slot:title>
        {{ alertProps.title }}
      </template>
      <p>{{ alertProps.message }}</p>
    </popup-alert>
  </div>
</template>

<script>
import { funnelStudioReturn } from '@/router/utils/funnel-studio-context';
import {
  required,
  minLength,
  maxLength,
  requiredIf,
  url,
} from 'vuelidate/lib/validators';
import Funnel from '@/models/Funnel';
import Product from '@/models/Product';
import CheckoutLink from '@/models/CheckoutLink';
import Notify from '@groovepages/gd-ui-kit/notify';
import axios from 'axios';
import moment from 'moment';
import ShoppingCartIcon from '@/assets/images/icons/ShoppingCartIcon.svg';
import PencilIcon from '@/assets/images/icons/PencilIcon.svg';
import ConfigStep from './steps/ConfigStep';
import StepsStep from './steps/StepsStep';
import PricePointsStep from './steps/PricePointsStep';
import LookFeelStep from './steps/LookFeelStep';
import FormFieldsStep from './steps/FormFieldsStep';
import PasswordsStep from './steps/PasswordsStep';
import BillingAdressStep from './steps/BillingAdressStep';
import CustomCssStep from './steps/CustomCssStep';
import LinksStep from './steps/LinksStep';

import NavigationHeader from './partials/NavigationHeader';
import ModernCheckoutLinkModal from './modals/ModernCheckoutLinkModal';
import SteperHeader from './partials/SteperHeader';
import { orderPricePointsByOptions, buildPricingOptions } from './pricingOptionsOrder';

import '@/assets/scss/products/checkout_links.scss';


export default {
  middleware: 'auth',
  scrollToTop: true,
  metaInfo() {
    return { title: 'Checkout Links' };
  },

  components: {
    ConfigStep,
    StepsStep,
    PricePointsStep,
    LookFeelStep,
    FormFieldsStep,
    PasswordsStep,
    BillingAdressStep,
    CustomCssStep,
    LinksStep,
    NavigationHeader,
    ModernCheckoutLinkModal,
    SteperHeader,
    ShoppingCartIcon,
    PencilIcon,
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },

      multi_step_redirect_options: {
        order_form_redirects: {
          order_form_url: {
            required: requiredIf(function (value) {
              const self = this;
              if (self.isMultiStepRedirect && self.selectedMultiStepType == 3) {
                return true;
              }
              return false;
            }),
            url,
          },
        },
        email_form_redirects: {
          billing_form_url: {
            required: requiredIf(function (value) {
              const self = this;

              if (self.isMultiStepRedirect && self.selectedMultiStepType == 2) {
                return true;
              }
              return false;
            }),
            url,
          },
          payment_form_url: {
            required: requiredIf(function (value) {
              const self = this;
              if (self.isMultiStepRedirect && self.selectedMultiStepType == 2) {
                return true;
              }
              return false;
            }),
            url,
          },
          password_form_url: {
            required: requiredIf(function (value) {
              const self = this;
              if (
                self.isMultiStepRedirect &&
                self.selectedMultiStepType == 2 &&
                self.model.checkout_link_page_options.move_password_to_last_step
              ) {
                return true;
              }
              return false;
            }),
            url,
          },
        },
        contact_form_redirects: {
          billing_form_url: {
            required: requiredIf(function (value) {
              const self = this;
              if (self.isMultiStepRedirect && self.selectedMultiStepType == 1) {
                return true;
              }
              return false;
            }),
            url,
          },
          payment_form_url: {
            required: requiredIf(function (value) {
              const self = this;
              if (self.isMultiStepRedirect && self.selectedMultiStepType == 1) {
                return true;
              }
              return false;
            }),
            url,
          },
          password_form_url: {
            required: requiredIf(function (value) {
              const self = this;
              if (
                self.isMultiStepRedirect &&
                self.selectedMultiStepType == 1 &&
                self.model.checkout_link_page_options.move_password_to_last_step
              ) {
                return true;
              }
              return false;
            }),
            url,
          },
        },
      },
    },
  },

  watch: {
    selectedProduct() { },
    '$route.fullPath'(nextPath, previousPath) {
      if (nextPath !== previousPath && this.$route.name === 'productsCheckoutLinks') {
        this.loadCheckoutLinks();
      }
    },
  },

  data() {
    return {
      alertProps: {
        show: false,
        variant: 'success',
        title: '',
        message: '',
      },
      isStepsValid: false,
      errorTabs: [],
      disableBrandingBar: 0,
      showSkipBillingAddress: true,
      currentTab: 'ConfigStep',
      funnel: null,
      selectedProduct: null,
      funnels: [],
      products: [],
      selectedFunnelId: 0,
      selectedProductId: 0,
      object: {
        name: 'Options',
      },
      checkoutFlow: 0,
      selectedCheckoutFlow: 0,
      trackingFlow: 0,
      selectedTrackingFlow: 0,
      proofWidgets: [],
      checkoutLinksIndex: 0,
      model: {
        isEdit: false,
        checkout_links: [],
        name: '',
        skip_field_arr: [],
        force_affiliate: 0,
        customization_details: [],
        post_transaction_url_details: [],
        is_multi_step_redirect: 0,
        multi_step_redirect_options: [],
        // force_affiliate : false,
        force_affiliate_id: null,
        primary_color: '#ffcc24',
        secondary_color: '#033a7c',
        step_type: this.selectedMultiStepType,
        checkout_link_type: this.selectedCheckoutTypeFreePaid,
      },
      multiPricePointLink: null,
      selectedCheckoutStepType: 1,
      selectedCheckoutTypeFreePaid: 1,
      selectedMultiStepType: 3,
      isMultiStepRedirect: 0,
      checkoutStepTypes: [
        {
          id: 1,
          title: '1-Step',
          description: '',
        },
        {
          id: 0,
          title: 'Multi-step',
          description: '',
        },
      ],


      selectedProductPricePoints: [],
      pricePointsTab: '',
      proofWidgetsTab: '',
      checkoutEmbedTypes: [
        { title: this.$t('frontend_product_hosted_checkout'), value: 'link' },
        { title: this.$t('frontend_product_embedded_into'), value: 'embed' },
      ],
      checkoutEmbedType: 'link',
      pricepointsForProjections: [],
      projectionPricePointsTab: '',
      postValidationTab: 'StepsStep',
      changeTabsAfterValidation: 1,
      checkoutLinks: [],
      selectedCheckoutData: [],
      checkout_link: null,
      checkout_embed: null,
      move_password_to_last_step: false,
      checkoutModalTitle: '',
      productHasOnlyFreePricePoints: true,
      loadError: null,
      productPricePointCount: 0,
      showCheckoutPagesModal: false,
      isSavingCheckoutStep: false,
      checkoutLinkToDelete: null,
      openCheckoutMenuId: null,
      showDeleteCheckoutModal: false,
      showCheckoutLinkModal: false,
    };
  },

  computed: {
    studioReturn() { return funnelStudioReturn(this.$route); },
    checkoutTemplateOptions() {
      return [
        {
          label: 'Default Sidebar',
          value: 1,
        },
        {
          label: 'Blue Sidebar',
          value: 2,
        },
        {
          label: 'Grey Sidebar',
          value: 3,
        },
        {
          label: 'Custom',
          value: 4,
        },
      ];
    },

    isAdmin() {
      return this.$store.state.user.admin;
    },

    tabs() {
      const createTab = (titleKey, value, description) => ({
        label: this.$t(titleKey),
        title: `<span class="text-sm font-semibold">${this.$t(titleKey)} <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i></span>`,
        value,
        description,
      });

      const baseTabs = [
        createTab('frontend_config', 'ConfigStep', 'Name, checkout type, and affiliate access'),
        createTab('frontend_steps', 'StepsStep', 'One-step or multi-step checkout flow'),
        createTab('frontend_price_points', 'PricePointsStep', 'Choose the offers available in this checkout'),
        createTab('frontend_product_step5_look_feel', 'LookFeelStep', 'Template, colors, typography, and legal settings'),
        createTab('frontend_product_step5_form_fields', 'FormFieldsStep', 'Choose the customer information to collect'),
        createTab('frontend_passwords', 'PasswordsStep', 'Account and password collection rules'),
      ];

      if (this.selectedFreePricePoints) {
        baseTabs.push(createTab('frontend_product_skip_billing_address', 'BillingAdressStep', 'Free-checkout billing and payment behavior'));
      }

      baseTabs.push(createTab('frontend_product_step5_custom_css', 'CustomCssStep', 'Custom styling and tracking code'));
      baseTabs.push({
        label: 'Links',
        title: '<span class="text-sm font-semibold">Links<span>',
        value: 'LinksStep',
        description: 'Copy the hosted, embedded, or popup checkout',
      });

      return baseTabs;
    },

    currentCheckoutTabIndex() {
      const index = this.tabs.findIndex((tab) => tab.value === this.currentTab);
      return index === -1 ? 0 : index;
    },

    activeCheckoutTab() {
      return this.tabs[this.currentCheckoutTabIndex] || null;
    },

    freePricePoints() {
      if (this.selectedProduct && this.selectedProduct.price_points) {
        return this.selectedProduct.price_points.filter((pricePoint) => {
          return pricePoint.price == 0;
        }).map((obj) => {
          return obj.id;
        });
      }
      return [];
    },

    selectedFreePricePoints() {
      if (this.model.hasOwnProperty('pricing_options') && this.model.pricing_options.length > 0) {
        const selectedPricePointIds = this.model.pricing_options
          .filter((pricePoint) => {
            return (
              pricePoint.enabled &&
              (pricePoint.enabled == true || pricePoint.enabled == 'true')
            );
          })
          .map((obj) => {
            return obj.id;
          });

        return this.freePricePoints.some((item) =>
          selectedPricePointIds.includes(item)
        );
      }

      return false;
    },



    pricePointsTabs() {
      const pricePointTabs = [];

      for (let i = 0; i < this.selectedProductPricePoints.length; i += 1) {
        pricePointTabs.push({
          title: this.selectedProductPricePoints[i].name,
          value: `price_point_${this.selectedProductPricePoints[i].id}`,
          price: this.selectedProductPricePoints[i].price,
        });
      }

      return pricePointTabs;
    },

    proofWidgetsTabs() {
      const proofWidgetsTabs = [];

      for (let i = 0; i < this.proofWidgets.length; i += 1) {
        proofWidgetsTabs.push({
          title: this.proofWidgets[i].name,
          value: `proof_widget_${this.proofWidgets[i].id}`,
        });
      }

      return proofWidgetsTabs;
    },

    projectionPricePointsTabs() {
      const pricePointTabs = [];

      for (let i = 0; i < this.pricepointsForProjections.length; i += 1) {
        pricePointTabs.push({
          title: this.pricepointsForProjections[i].name,
          value: `price_point_${this.pricepointsForProjections[i].id}`,
        });
      }
      return pricePointTabs;
    },

  },

  created() {
    this.loadCheckoutLinks();
  },

  methods: {
    async loadCheckoutLinks() {
      this.loadError = null;
      this.$wait.start('products');

      try {
        const funnel = await Funnel.$find(this.$route.params.id);
        const selectedProduct = await Product.$find(this.$route.params.product_id);
        const [checkoutLinks, pricePoints, funnels] = await Promise.all([
          selectedProduct.checkoutLinks().$get(),
          selectedProduct.pricepoints().$get(),
          Funnel.$get(),
        ]);

        this.funnel = funnel;
        this.selectedProduct = selectedProduct;
        this.checkoutLinks = checkoutLinks;
        this.productPricePointCount = pricePoints.length;
        this.funnels = funnels;
        this.selectedFunnelId = this.$route.params.id;
        this.selectedProductId = this.$route.params.product_id;
      } catch (error) {
        const response = error && error.response ? error.response : {};
        const responseMessage = response.data && response.data.error
          ? String(response.data.error).toLowerCase()
          : '';
        const isAccessError = response.status === 401
          || response.status === 403
          || responseMessage.includes('unauthorized');

        this.funnel = null;
        this.selectedProduct = null;
        this.checkoutLinks = [];
        this.productPricePointCount = 0;
        this.funnels = [];
        this.loadError = isAccessError
          ? {
            title: 'This product is not available in your current Groove account',
            message: 'Return to your funnels and choose a product from this account, or switch accounts and try again.',
          }
          : {
            title: 'We could not load these checkout links',
            message: 'The checkout service may be temporarily unavailable. Try again, or return to your funnels and reopen the product.',
          };
      } finally {
        this.$wait.end('products');
      }
    },

    retryCheckoutLinks() {
      return this.loadCheckoutLinks();
    },

    goToFunnels() {
      this.$router.push(this.studioReturn || { name: 'funnels' });
    },

    normalizeCheckoutPageOptions(checkoutLink) {
      if (!checkoutLink || !checkoutLink.checkout_link_page_options) {
        return {};
      }

      if (typeof checkoutLink.checkout_link_page_options === 'string') {
        try {
          return JSON.parse(checkoutLink.checkout_link_page_options);
        } catch (e) {
          return {};
        }
      }

      return checkoutLink.checkout_link_page_options;
    },

    checkoutExperienceLabel(checkoutLink) {
      return Number(checkoutLink && checkoutLink.is_onestep) === 0
        ? 'Multi-step checkout'
        : 'Single-step checkout';
    },

    checkoutTemplateLabel(checkoutLink) {
      const options = this.normalizeCheckoutPageOptions(checkoutLink);
      const lookAndFeel = options.look_and_feel || {};
      const templateType = Number(lookAndFeel.template_type || 1);
      const template = this.checkoutTemplateOptions.find((option) => option.value === templateType);
      return template ? template.label : 'Default Sidebar';
    },

    checkoutDisplayName(checkoutLink) {
      if (checkoutLink && checkoutLink.name) {
        return checkoutLink.name;
      }

      if (checkoutLink && checkoutLink.permalink) {
        return `Checkout ${checkoutLink.permalink}`;
      }

      return 'Untitled checkout';
    },

    checkoutDescription(checkoutLink) {
      const productName = this.selectedProduct && this.selectedProduct.name
        ? this.selectedProduct.name
        : 'this product';
      const type = this.checkoutExperienceLabel(checkoutLink).toLowerCase();
      return `${this.checkoutTemplateLabel(checkoutLink)} ${type} for ${productName}.`;
    },

    checkoutPricePointSummary(checkoutLink) {
      const pricingOptions = checkoutLink && Array.isArray(checkoutLink.pricing_options)
        ? checkoutLink.pricing_options
        : [];
      const enabledPricePoints = pricingOptions.filter((pricePoint) => {
        return pricePoint && (pricePoint.enabled === true || pricePoint.enabled === 'true' || pricePoint.enabled === 1);
      });

      if (enabledPricePoints.length) {
        return `${enabledPricePoints.length} enabled`;
      }

      if (pricingOptions.length) {
        return 'None enabled';
      }

      return 'All price points';
    },

    checkoutUrlSummary(checkoutLink) {
      if (!checkoutLink) {
        return 'Not generated yet';
      }

      if (checkoutLink.has_custom_domain && checkoutLink.custom_domain) {
        return checkoutLink.custom_domain;
      }

      if (checkoutLink.subdomain) {
        return `${checkoutLink.subdomain} checkout`;
      }

      if (checkoutLink.permalink) {
        return checkoutLink.permalink;
      }

      return 'Not generated yet';
    },

    checkoutUpdatedLabel(checkoutLink) {
      const dateTime = checkoutLink && (checkoutLink.updated_at || checkoutLink.created_at);
      const parsed = moment(dateTime);

      return parsed.isValid()
        ? parsed.format('MMM D, YYYY')
        : 'Not available';
    },

    openNewCheckoutPage() {
      return this.openCheckoutPagesModal();
    },

    openCheckoutBuilder(checkoutLink) {
      return this.openEditCheckoutPage(checkoutLink.id);
    },

    handleTabChange() {
      //  this.currentTab = newTab;
    },


    getSiblingTab(position = 1) {
      const currentlySelectedTabIndex = this.tabs
        .map((item) => item.value)
        .indexOf(this.currentTab);

      const tab = this.tabs[currentlySelectedTabIndex + position];
      return tab ? tab.value : this.currentTab;
    },

    moveNextOnWizard() {
      const nextTab = this.getSiblingTab(1);
      this.postValidationTab = nextTab;
      const refToValidate = this.$refs[this.currentTab];
      this.errorTabs = [];
      const validTabs = ['ConfigStep', 'StepsStep', 'PricePointsStep', 'LookFeelStep', 'FormFieldsStep', 'PasswordsStep', 'BillingAdressStep', 'CustomCssStep'];

      if (validTabs.includes(this.currentTab)) {

        if (this.currentTab === 'PasswordsStep') {
          if (!this.isStepsValid) {
            this.errorTabs.push('StepsStep');
            return false;
          }
          return refToValidate.validate();
        }
        return refToValidate.validate();
      }

      // if its create checkout functionality
      if (!this.model.isEdit) {
        this.currentTab = nextTab;
      }

      return true;
    },

    moveBackOnWizard() {
      const prevTab = this.getSiblingTab(-1);
      this.postValidationTab = prevTab;
      this.currentTab = prevTab;
    },

    selectCheckoutWizardTab(tab) {
      const targetIndex = this.tabs.findIndex((item) => item.value === tab.value);

      if (targetIndex !== -1) {
        this.currentTab = tab.value;
        this.postValidationTab = tab.value;
      }
    },

    async closeCheckoutWizard() {
      this.showCheckoutPagesModal = false;
      // The wizard saves pricing_options in place, but the dashboard cards are
      // backed by the original checkoutLinks collection. Reload it before the
      // user sees the cards again so enabled counts and updated metadata agree
      // with the saved checkout.
      await this.loadCheckoutLinks();
    },

    async openEditCheckoutPage(id) {
      this.$loader.start('openEditCheckoutPage');
      this.currentTab = 'ConfigStep';
      this.selectedCheckoutData = await CheckoutLink.$find(id);
      const normalizedPageOptions = this.normalizeCheckoutPageOptions(this.selectedCheckoutData);
      this.selectedCheckoutData.checkout_link_page_options = {
        ...normalizedPageOptions,
        look_and_feel: normalizedPageOptions.look_and_feel || { template_type: 1 },
        product_image: normalizedPageOptions.product_image || '',
        tracking_codes: normalizedPageOptions.tracking_codes || '',
        move_field_to_next_step: !!normalizedPageOptions.move_field_to_next_step,
        move_password_to_last_step: !!normalizedPageOptions.move_password_to_last_step,
      };
      this.selectedCheckoutData.pricing_options = Array.isArray(this.selectedCheckoutData.pricing_options)
        ? this.selectedCheckoutData.pricing_options
        : [];

      if (this.selectedCheckoutData.checkout_link_page_options.hasOwnProperty('move_field_to_next_step')) {
        this.move_field_to_next_step = this.selectedCheckoutData.checkout_link_page_options.move_field_to_next_step;
      } else {
        this.selectedCheckoutData.checkout_link_page_options.move_field_to_next_step = false;
      }

      let formFields = this.selectedCheckoutData.form_fields;
      if (this.selectedCheckoutData.hasOwnProperty('form_fields') && (formFields == null || formFields == 'null' || formFields == undefined)) {
        if (this.selectedProduct.checkout_page_options.hasOwnProperty('form_fields')) {
          formFields = this.selectedProduct.checkout_page_options.form_fields;
        }
      }

      if (this.selectedCheckoutData.checkout_link_page_options.hasOwnProperty('move_password_to_last_step')) {
        this.move_password_to_last_step = this.selectedCheckoutData.checkout_link_page_options.move_password_to_last_step;
      } else {
        this.selectedCheckoutData.checkout_link_page_options.move_password_to_last_step = false;
      }
      this.selectedCheckoutStepType = this.selectedCheckoutData.is_onestep;
      this.selectedCheckoutTypeFreePaid = this.selectedCheckoutData.checkout_link_type;
      this.selectedMultiStepType = this.selectedCheckoutData.step_type;
      this.isMultiStepRedirect = this.selectedCheckoutData.is_multi_step_redirect;
      const productId = this.$route.params.product_id;
      this.selectedProduct = await Product.$find(productId);
      this.selectedProduct.checkout_page_options = this.selectedProduct.checkout_page_options || {};
      const currentProductPricePoints = await this.selectedProduct.pricepoints().$get();
      const selectedPricePointData = this.setupModel(currentProductPricePoints, formFields);

      this.selectedProductPricePoints = [];
      const self = this;
      const orderedProductPricePoints = orderPricePointsByOptions(
        currentProductPricePoints,
        this.selectedCheckoutData.pricing_options,
      );
      orderedProductPricePoints.forEach((val) => {
        const selectedPricePoint = selectedPricePointData.find((pricePoint) => pricePoint.id == val.id);
        if (selectedPricePoint != undefined) {
          val.enabled = selectedPricePoint.enabled;

          if (this.selectedCheckoutData.checkout_link_type == 1 && val.price > 0) {
            self.selectedProductPricePoints.push(val);
          }

          if (this.selectedCheckoutData.checkout_link_type == 2 && val.price == 0) {
            self.selectedProductPricePoints.push(val);
          }
        } else {
          if (this.selectedCheckoutData.pricing_options == null) {
            this.selectedCheckoutData.pricing_options = [];
          }

          if (val.id != undefined || val.id != null) {
            this.selectedCheckoutData.pricing_options.push({
              ask_for_password: false,
              ask_username: false,
              enabled: false,
              id: val.id,
              skip_billing_info: false,
              skip_payment_info: false,
              skip_terms_or_service: false,
            });
            self.selectedProductPricePoints.push(val);
          }
        }
      });


      if (!self.selectedProductPricePoints.length) {
        self.selectedProductPricePoints = orderedProductPricePoints;
      }

      this.pricePointsTab = self.selectedProductPricePoints.length
        ? `price_point_${self.selectedProductPricePoints[0].id}`
        : '';

      this.selectedProduct.checkout_page_options.product_image =
        this.selectedCheckoutData.checkout_link_page_options.product_image !== undefined
          ? this.selectedCheckoutData.checkout_link_page_options.product_image
          : '';

      this.selectedProduct.checkout_page_options.tracking_codes =
        this.selectedCheckoutData.checkout_link_page_options.tracking_codes !== undefined
          ? this.selectedCheckoutData.checkout_link_page_options.tracking_codes
          : '';

      this.selectedProduct.checkout_page_options.look_and_feel =
        this.selectedCheckoutData.checkout_link_page_options.look_and_feel !== undefined
          ? this.selectedCheckoutData.checkout_link_page_options.look_and_feel
          : '';


      if (this.selectedProduct.checkout_page_options.look_and_feel.template_type != undefined && this.selectedProduct.checkout_page_options.look_and_feel.template_type == 4) {
        if (this.selectedProduct.checkout_page_options.look_and_feel.custom_color != undefined) {
          this.model.primary_color = this.selectedProduct.checkout_page_options.look_and_feel.custom_color.primary_color;
        }
        if (this.selectedProduct.checkout_page_options.look_and_feel.custom_color != undefined) {
          this.model.secondary_color = this.selectedProduct.checkout_page_options.look_and_feel.custom_color.secondary_color;
        }
      }

      if (this.selectedCheckoutData.hasOwnProperty('pricing_options') && this.selectedCheckoutData.pricing_options != null) {
        this.model.skip_field_arr = currentProductPricePoints.map(
          (point) => {
            const pricePointOptions = this.selectedCheckoutData.pricing_options.find((option) => option.id == point.id) || {};
            let skip_payment = pricePointOptions.skip_payment_info;
            if (point.price == 0) {
              skip_payment = true;
            }

            let returnData = {
              id: point.id,
              ask_username: !!pricePointOptions.ask_username,
              ask_for_password: !!pricePointOptions.ask_for_password,
              ask_for_password_end: !!pricePointOptions.ask_for_password_end,
              skip_billing_info: !!pricePointOptions.skip_billing_info,
              skip_payment_info: skip_payment,
              skip_terms_or_service: !!pricePointOptions.skip_terms_or_service,

            }
            if (pricePointOptions.hasOwnProperty('skip_phone') && pricePointOptions.skip_phone != null)
              returnData['skip_phone'] = pricePointOptions.skip_phone;
            else
              returnData['skip_phone'] = false;

            return returnData;
          }
        );
      }
      this.model = { ...this.model };
      this.pricepointsForProjections = await this.selectedProduct.pricepoints().$get();
      this.projectionPricePointsTab = this.pricepointsForProjections.length
        ? `price_point_${this.pricepointsForProjections[0].id}`
        : '';
      this.checkoutModalTitle = `Edit checkout: ${this.selectedCheckoutData.name || 'Untitled checkout'}`;
      this.showCheckoutPagesModal = true;
      this.$loader.end('openEditCheckoutPage');
    },
    setupModel(currentProductPricePoints, formFields) {
      this.model = this.selectedCheckoutData;
      this.model.isEdit = true;
      this.model.primary_color = this.selectedCheckoutData.primary_color;
      this.model.secondary_color = this.selectedCheckoutData.secondary_color;
      this.model.ask_for_password_select = 0;
      this.model.form_fields = formFields;
      this.model.step_type = this.selectedCheckoutData.step_type;
      this.model.customization_details = this.selectedCheckoutData.customization_details;
      this.model.multi_step_redirect_options = this.selectedCheckoutData.multi_step_redirect_options;
      this.model.is_multi_step_redirect = this.selectedCheckoutData.is_multi_step_redirect;
      this.model.post_transaction_url_details = this.selectedCheckoutData.post_transaction_url_details;
      if (this.model.post_transaction_url_details == null) {
        const postTransactionDetail = [];
        currentProductPricePoints.forEach((val) => {
          const postTransactionDetails = val.post_transaction_details || {};
          postTransactionDetail.push({
            id: val.id,
            name: val.name,
            post_transaction_type: postTransactionDetails.type,
            post_transaction_url: postTransactionDetails.post_transaction_url,
          });
        });
        this.model.post_transaction_url_details = postTransactionDetail;
      }
      const selectedPricePointData = [];
      if (this.selectedCheckoutData.pricing_options != undefined) {
        for (let i = 0; i < this.selectedCheckoutData.pricing_options.length; i++) {
          if (this.model.ask_for_password_select == 0) {
            if (this.selectedCheckoutData.pricing_options[i].ask_for_password) {
              this.model.ask_for_password_select = 1;
            }

            if (this.selectedCheckoutData.pricing_options[i].ask_for_password_end) {
              this.model.ask_for_password_select = 2;
            }
          }

          selectedPricePointData.push({
            id: this.selectedCheckoutData.pricing_options[i].id,
            enabled: this.selectedCheckoutData.pricing_options[i].enabled,
          });
        }
      }
      return selectedPricePointData;
    },

    async openCheckoutPagesModal() {
      this.$loader.start('openCheckoutPagesModal');
      this.setLocalProperties();
      this.productHasOnlyFreePricePoints = true;
      const productId = this.$route.params.product_id;

      this.selectedProduct = await this.funnel.products().$find(productId);
      this.selectedProduct.checkout_page_options = this.selectedProduct.checkout_page_options || {};
      this.selectedProduct.checkout_page_options.look_and_feel = this.selectedProduct.checkout_page_options.look_and_feel || { template_type: 1 };
      const selectedCheckoutLinks = await this.selectedProduct.checkoutLinks().$get();
      const currentProductPricePoints = await this.selectedProduct.pricepoints().$get();

      if (!currentProductPricePoints.length) {
        this.alertProps = {
          show: true,
          variant: 'error',
          title: 'Add a price point first',
          message: 'A checkout needs at least one price point before it can be created.',
        };
        this.$loader.end('openCheckoutPagesModal');
        return;
      }

      for (let i = 0; i < currentProductPricePoints.length; i++) {
        if (currentProductPricePoints[i].price !== 0) {
          this.productHasOnlyFreePricePoints = false;
        }

        this.model.skip_field_arr.splice(i, 0, {
          id: currentProductPricePoints[i].id,
          enabled: false,
          ask_username: false,
          ask_for_password: false,
          ask_for_password_end: false,
          skip_billing_info: false,
          skip_payment_info: false,
          skip_terms_or_service: false,
          skip_phone: false,
        });
      }

      this.pricepointsForProjections = await this.selectedProduct.pricepoints().$get();
      this.projectionPricePointsTab = `price_point_${this.pricepointsForProjections[0].id}`;
      this.selectedProductPricePoints = []; // this prop passes in step 2
      const self = this;
      currentProductPricePoints.forEach((val) => {
        val.enabled = true;
        self.selectedProductPricePoints.push(val);
      });

      this.pricePointsTab = `price_point_${self.selectedProductPricePoints[0].id}`;
      this.model.checkout_links = selectedCheckoutLinks;
      this.model.checkout_link_page_options = {
        move_field_to_next_step: false,
        move_password_to_last_step: false,
      };
      this.checkoutFlow = 'Single Price Checkout';
      this.selectedCheckoutFlow = 0;
      this.selectedCheckoutTypeFreePaid = this.productHasOnlyFreePricePoints ? 2 : 1;
      this.model.step_type = 1;
      this.multiPricePointLink = null;
      this.model.ask_for_password_select = 0;

      if (this.selectedProduct.checkout_page_options.look_and_feel.template_type == 4) {
        this.model.primary_color = this.selectedProduct.checkout_page_options.look_and_feel.custom_color.primary_color;
        this.model.secondary_color = this.selectedProduct.checkout_page_options.look_and_feel.custom_color.secondary_color;
      }
      this.model.multi_step_redirect_options = {
        order_form_redirects: {
          order_form_url: '',
        },
        email_form_redirects: {
          billing_form_url: '',
          password_form_url: '',
          payment_form_url: '',
        },
        contact_form_redirects: {
          billing_form_url: '',
          password_form_url: '',
          payment_form_url: '',
        },
      };

      this.model = { ...this.model };
      this.checkoutModalTitle = `Create checkout: ${this.selectedProduct.name}`;
      this.showCheckoutPagesModal = true;
      this.$loader.end('openCheckoutPagesModal');
    },

    async stepValidationSuccessful(name, isValid) {
      if (name == 'StepsStep') {
        this.isStepsValid = isValid;
      }
      if (!isValid) {
        return false;
      }

      try {
        if (this.model.isEdit) {
          await this.updateStepData(name);
        } else if (this.changeTabsAfterValidation) {
          await this.saveStepData(name);
        }
      } catch (error) {
        this.alertProps = {
          show: true,
          variant: 'error',
          title: 'Changes were not saved',
          message: 'Please check your connection and try saving this step again.',
        };
        return false;
      } finally {
        this.isSavingCheckoutStep = false;
        this.$loader.end('saveStepData');
        this.$loader.end('updateStepData');
      }

      return true;
    },
    async saveStepData(name) {
      this.isSavingCheckoutStep = true;
      switch (name) {
        case 'ConfigStep':
          this.$loader.start('saveStepData');
          this.selectedCheckoutData.checkout_link_type = this.selectedCheckoutTypeFreePaid;
          const self = this;
          this.model.skip_field_arr.forEach((element, index) => {
            if (self.freePricePoints.includes(element.id)) {
              this.model.skip_field_arr[index].enabled = this.selectedCheckoutTypeFreePaid == 1 ? false : true
            } else {
              this.model.skip_field_arr[index].enabled = this.selectedCheckoutTypeFreePaid == 1 ? true : false
            }
          });

          const newProductCheckoutData = {
            product_id: this.selectedProduct.id,
            checkout_link_type: this.selectedCheckoutTypeFreePaid,
            price_point_id: 0,
            name: this.model.name,
            pricing_options: this.model.skip_field_arr,
            is_onestep: this.selectedCheckoutStepType,
            step_type: this.selectedMultiStepType,
            is_multi_step_redirect: 0,
            is_manual: 1,
            checkout_link_page_options: {
              look_and_feel: this.selectedProduct.checkout_page_options.look_and_feel,
              product_image: this.selectedProduct.checkout_page_options.product_image,
              tracking_codes: this.selectedProduct.checkout_page_options.tracking_codes,
              move_field_to_next_step: this.model.checkout_link_page_options.move_field_to_next_step,
              move_password_to_last_step: this.model.checkout_link_page_options.move_password_to_last_step,
            },
            force_affiliate: this.model.force_affiliate,
            force_affiliate_id: this.model.force_affiliate_id,
            form_fields: this.selectedProduct.checkout_page_options.form_fields,
          };


          const newMultiPricePointLink = new CheckoutLink(newProductCheckoutData);
          const savedCheckoutLinkData = await newMultiPricePointLink.save();
          this.selectedCheckoutData = await CheckoutLink.$find(savedCheckoutLinkData.data.id);
          const selectedProduct = await Product.$find(this.$route.params.product_id);
          const currentProductPricePoints = await selectedProduct.pricepoints().$get();


          const selectedPricePointData = [];
          if (this.selectedCheckoutData.pricing_options != undefined) {
            for (let i = 0; i < this.selectedCheckoutData.pricing_options.length; i++) {
              selectedPricePointData.push({
                id: this.selectedCheckoutData.pricing_options[i].id,
                enabled: this.selectedCheckoutData.pricing_options[i].enabled,
              });
            }
          }
          this.selectedProductPricePoints = [];
          currentProductPricePoints.forEach((val, index) => {
            if (selectedPricePointData[index] != undefined) {
              if (selectedPricePointData.length != 0 && selectedPricePointData[index].id == val.id) {
                val.enabled = selectedPricePointData[index].enabled;
              }
              if (this.selectedCheckoutData.checkout_link_type == 1 && val.price > 0) {
                self.selectedProductPricePoints.push(val);
              }
              if (this.selectedCheckoutData.checkout_link_type == 2 && val.price == 0) {
                self.selectedProductPricePoints.push(val);
              }
            }
          });

          this.checkoutLinks = await selectedProduct.checkoutLinks().$get();
          this.currentTab = 'StepsStep';
          this.model = this.selectedCheckoutData;
          this.model.isEdit = true;

          if (this.selectedProduct.checkout_page_options.look_and_feel.template_type != undefined && this.selectedProduct.checkout_page_options.look_and_feel.template_type == 4) {
            if (this.selectedProduct.checkout_page_options.look_and_feel.custom_color != undefined) {
              this.model.primary_color = this.selectedProduct.checkout_page_options.look_and_feel.custom_color.primary_color;
            }
            if (this.selectedProduct.checkout_page_options.look_and_feel.custom_color != undefined) {
              this.model.secondary_color = this.selectedProduct.checkout_page_options.look_and_feel.custom_color.secondary_color;
            }
          }

          this.model.ask_for_password_select = 0;
          const savedPricingOptionsForSkipFields = this.selectedCheckoutData.pricing_options || [];
          this.model.skip_field_arr = currentProductPricePoints.map(
            (point) => {
              const pricingOption = savedPricingOptionsForSkipFields.find(
                option => option && String(option.id) === String(point.id),
              ) || {};
              return {
                id: point.id,
                ask_username: pricingOption.ask_username,
                ask_for_password: pricingOption.ask_for_password,
                ask_for_password_end: pricingOption.ask_for_password_end,
                skip_billing_info: pricingOption.skip_billing_info,
                skip_payment_info: pricingOption.skip_payment_info,
                skip_terms_or_service: pricingOption.skip_terms_or_service,
              };
            }
          );
          this.model = { ...this.model };
          this.$loader.end('saveStepData');
          break;
      }
      this.currentTab = this.postValidationTab;
      this.isSavingCheckoutStep = false;
    },
    async updateStepData(name) {

      this.isSavingCheckoutStep = true;
      this.$loader.start('updateStepData');
      switch (name) {
        case 'ConfigStep':
          this.selectedCheckoutData.name = this.model.name;
          this.selectedCheckoutData.checkout_link_type = this.selectedCheckoutTypeFreePaid;
          const self = this;
          if (this.selectedCheckoutTypeFreePaid == 1) {
            this.selectedCheckoutData.pricing_options.forEach(
              (element, index) => {
                if (self.freePricePoints.includes(element.id)) {
                  this.selectedCheckoutData.pricing_options[index].enabled = false;
                }
              }
            );
          } else {
            const self = this;
            this.selectedCheckoutData.pricing_options.forEach(
              (element, index) => {
                if (!self.freePricePoints.includes(element.id)) {
                  this.selectedCheckoutData.pricing_options[index].enabled = false;
                }
              }
            );
          }


          this.selectedCheckoutData.checkout_link_page_options.move_field_to_next_step = this.model.checkout_link_page_options.move_field_to_next_step;
          this.selectedCheckoutData.checkout_link_page_options.move_password_to_last_step = this.model.checkout_link_page_options.move_password_to_last_step;

          this.selectedCheckoutData.force_affiliate = this.model.force_affiliate;
          this.selectedCheckoutData.force_affiliate_id = this.model.force_affiliate_id;
          if (!this.model.force_affiliate) {
            this.selectedCheckoutData.force_affiliate_id = null;
          }
          await this.selectedCheckoutData.save();
          const selectedProduct = await Product.$find(this.$route.params.product_id);
          const currentProductPricePoints = await selectedProduct.pricepoints().$get();
          const savedPricingOptions = this.selectedCheckoutData.pricing_options || [];

          this.selectedProductPricePoints = []; // this prop passes in step 2 as pricePoints
          const orderedPricePoints = orderPricePointsByOptions(
            currentProductPricePoints,
            savedPricingOptions,
          );
          orderedPricePoints.forEach((val) => {
            const matchingOption = savedPricingOptions.find(
              option => option && String(option.id) === String(val.id),
            );
            if (matchingOption != undefined) {
              val.enabled = matchingOption.enabled;
              if (this.selectedCheckoutData.checkout_link_type == 1 && val.price > 0) {
                self.selectedProductPricePoints.push(val);
              }
              if (this.selectedCheckoutData.checkout_link_type == 2 && val.price == 0) {
                self.selectedProductPricePoints.push(val);
              }
            }
          });

          this.checkoutLinks = await selectedProduct.checkoutLinks().$get();
          break;

        case 'StepsStep':

          this.selectedCheckoutData.is_onestep = this.selectedCheckoutStepType;
          this.model.is_onestep = this.selectedCheckoutStepType;
          this.selectedCheckoutData.step_type = this.selectedMultiStepType;

          if (this.selectedCheckoutStepType == 1) {
            this.selectedCheckoutData.step_type = 1;
          }
          this.selectedCheckoutData.is_multi_step_redirect = this.isMultiStepRedirect ? 1 : 0;
          this.selectedCheckoutData.multi_step_redirect_options = this.model.multi_step_redirect_options;
          await this.selectedCheckoutData.save();
          break;

        case 'PricePointsStep':

          this.selectedCheckoutData.pricing_options = buildPricingOptions(
            this.selectedProductPricePoints,
            this.selectedCheckoutData.pricing_options,
          );


          await this.selectedCheckoutData.save();
          break;

        case 'LookFeelStep':
          this.selectedCheckoutData.customization_details = this.model.customization_details;
          this.selectedCheckoutData.disable_branding_bar = this.disableBrandingBar;
          this.selectedCheckoutData.checkout_link_page_options = {
            look_and_feel: this.selectedProduct.checkout_page_options.look_and_feel,
            product_image: this.selectedProduct.checkout_page_options.product_image,
            tracking_codes: this.selectedProduct.checkout_page_options.tracking_codes,
            move_field_to_next_step: this.selectedCheckoutData.checkout_link_page_options.move_field_to_next_step,
            move_password_to_last_step: this.selectedCheckoutData.checkout_link_page_options.move_password_to_last_step,
          };

          var { template_type } = this.selectedCheckoutData.checkout_link_page_options.look_and_feel;

          if (template_type != undefined && template_type == 4) {
            this.selectedCheckoutData.checkout_link_page_options.look_and_feel =
            {
              template_type: this.selectedCheckoutData.checkout_link_page_options.look_and_feel.template_type,
              custom_color: {
                primary_color: this.model.primary_color,
                secondary_color: this.model.secondary_color,
              },
            };
          } else {
            this.selectedCheckoutData.checkout_link_page_options.look_and_feel =
            {
              template_type: this.selectedCheckoutData.checkout_link_page_options.look_and_feel.template_type,
            };
          }

          await this.selectedCheckoutData.save();
          break;

        case 'FormFieldsStep':
          this.selectedCheckoutData.form_fields = this.model.form_fields;
          await this.selectedCheckoutData.save();
          break;
        case 'PasswordsStep':
          this.selectedCheckoutData.checkout_link_page_options.move_password_to_last_step = this.model.checkout_link_page_options.move_password_to_last_step;

          const skipById = new Map(
            (this.model.skip_field_arr || [])
              .filter(e => e && e.id != null)
              .map(e => [String(e.id), e]),
          );

          for (let i = 0; i < this.selectedCheckoutData.pricing_options.length; i++) {
            const pricingOption = this.selectedCheckoutData.pricing_options[i];

            if (!skipById.has(String(pricingOption.id))) {
              continue;
            }

            const pricePointSkipFields = skipById.get(String(pricingOption.id)) || {};

            let { ask_for_password } = pricePointSkipFields;
            let { ask_for_password_end } = pricePointSkipFields;
            const { ask_username } = pricePointSkipFields;

            if (this.model.has_password_fields == 0) {
              ask_for_password = false;
              ask_for_password_end = false;
            } else if (this.model.checkout_link_page_options.move_password_to_last_step) {
              ask_for_password = false;
              if (pricePointSkipFields.ask_for_password) {
                ask_for_password = true;
                ask_for_password_end = true;
              } else {
                ask_for_password_end = false;
              }
            } else {
              if (pricePointSkipFields.ask_for_password) {
                ask_for_password = true;
              } else {
                ask_for_password = false;
              }
              ask_for_password_end = false;
            }

            this.selectedCheckoutData.pricing_options[i] = {
              id: pricingOption.id,
              enabled: pricingOption.enabled,
              ask_username,
              ask_for_password,
              ask_for_password_start: ask_for_password,
              ask_for_password_end,
              skip_billing_info: pricePointSkipFields.skip_billing_info,
              skip_payment_info: pricePointSkipFields.skip_payment_info,
              skip_terms_or_service: pricePointSkipFields.skip_terms_or_service,
              skip_phone: pricePointSkipFields.skip_phone,
            };
          }

          this.selectedCheckoutData.has_password_fields = this.model.has_password_fields;
          await this.selectedCheckoutData.save();
          break;

        case 'BillingAdressStep':
          for (let i = 0; i < this.selectedCheckoutData.pricing_options.length; i++) {
            const pricePointSkipFieldsArray =
              (this.model.skip_field_arr || []).find((skipFieldsArray) => {
                return (
                  skipFieldsArray &&
                  skipFieldsArray.id ==
                  this.selectedCheckoutData.pricing_options[i].id
                );
              });

            if (pricePointSkipFieldsArray == undefined) {
              continue;
            }

            const { ask_for_password } = pricePointSkipFieldsArray;
            const { ask_for_password_end } = pricePointSkipFieldsArray;
            const { ask_username } = pricePointSkipFieldsArray;
            let { skip_billing_info } = pricePointSkipFieldsArray;
            let { skip_terms_or_service } = pricePointSkipFieldsArray;
            let { skip_phone } = pricePointSkipFieldsArray;

            if (this.model.has_skip_fields == 0) {
              skip_billing_info = false;
              skip_terms_or_service = false;
            }

            this.selectedCheckoutData.pricing_options[i] = {
              id: this.selectedCheckoutData.pricing_options[i].id,
              enabled: this.selectedCheckoutData.pricing_options[i].enabled,
              ask_username,
              ask_for_password,
              ask_for_password_start: ask_for_password,
              ask_for_password_end,
              skip_billing_info,
              skip_payment_info: pricePointSkipFieldsArray.skip_payment_info,
              skip_terms_or_service,
              skip_phone: skip_phone
            };
          }

          this.selectedCheckoutData.has_password_fields = this.model.has_password_fields;
          this.selectedCheckoutData.has_skip_fields = this.model.has_skip_fields;
          await this.selectedCheckoutData.save();
          break;

        case 'CustomCssStep':
          this.selectedCheckoutData.checkout_link_page_options = {
            look_and_feel: this.selectedProduct.checkout_page_options.look_and_feel,
            product_image: this.selectedProduct.checkout_page_options.product_image,
            tracking_codes: this.selectedProduct.checkout_page_options.tracking_codes,
            move_field_to_next_step: this.selectedCheckoutData.checkout_link_page_options.move_field_to_next_step,
            move_password_to_last_step: this.selectedCheckoutData.checkout_link_page_options.move_password_to_last_step,
          };

          var { template_type } = this.selectedCheckoutData.checkout_link_page_options.look_and_feel;
          if (template_type != undefined && template_type == 4) {
            this.selectedCheckoutData.checkout_link_page_options.look_and_feel =
            {
              template_type: this.selectedCheckoutData.checkout_link_page_options.look_and_feel.template_type,
              custom_color: {
                primary_color: this.model.primary_color,
                secondary_color: this.model.secondary_color,
              },
            };
          } else {
            this.selectedCheckoutData.checkout_link_page_options.look_and_feel =
            {
              template_type: this.selectedCheckoutData.checkout_link_page_options.look_and_feel.template_type,
            };
          }

          await this.selectedCheckoutData.save();
          break;
      }

      if (name === 'PasswordsStep') {
        if (!this.selectedFreePricePoints) {
          this.currentTab = 'CustomCssStep';
        }
        else {
          this.currentTab = 'BillingAdressStep';
        }
      } else {
        this.currentTab = this.getSiblingTab(1);
      }

      this.$loader.end('updateStepData');
      this.isSavingCheckoutStep = false;

    },

    handlePricePointsTabChange(newTab) {
      this.pricePointsTab = newTab;
    },

    handleProjectionPricePointsTabChange(newTab) {
      this.projectionPricePointsTab = newTab;
    },

    handleCheckoutEmbedTypesTabChange(newTab) {
      this.checkoutEmbedType = newTab;
    },

    showPricePointDetails(productId) {
      const selectedProduct = this.products
        .map((item) => item.id)
        .indexOf(productId);
      this.selectedProductPricePoints =
        this.products[selectedProduct].price_points;
      this.$root.$emit('modal-open', 'price-points-modal');
    },

    sendToFunnels() {
      this.$router.push({ name: 'funnels' });
    },

    showCheckoutLinks() {
      this.selectedCheckoutFlow = this.checkoutFlow;
    },

    showTrackingCode() {
      this.selectedTrackingFlow = this.trackingFlow;
    },

    showCheckoutOptions() {
      this.checkoutFlow = 0;
      this.selectedCheckoutFlow = 0;
    },

    showTrackingOptions() {
      this.trackingFlow = 'Tracking Link';
      this.selectedTrackingFlow = 0;
    },

    async openProjectionsModal(productId) {
      this.$loader.start('openProjectionsModal');
      this.selectedProduct = await this.funnel.products().$find(productId);
      this.pricepointsForProjections = await this.selectedProduct
        .pricepoints()
        .$get();

      this.projectionPricePointsTab = `price_point_${this.pricepointsForProjections[0].id}`;
      this.$root.$emit('modal-open', 'projections-modal');
      this.$loader.end('openProjectionsModal');
    },

    async savePricePointProjections() {
      this.$loader.start('savePricePointProjections');

      const promises = [];
      for (let i = 0; i < this.pricepointsForProjections.length; i++) {
        promises.push(this.pricepointsForProjections[i].save());
      }

      const self = this;
      await Promise.all(promises).then((values) => {
        self.$root.$emit('modal-close', 'projections-modal');
        this.$loader.end('savePricePointProjections');
      });
    },

    async openTrackingModal(productId) {
      this.$loader.start('openTrackingModal');
      this.selectedProduct = await this.funnel.products().$find(productId);
      this.proofWidgets = await this.selectedProduct.proof_widgets().$get();
      if (this.proofWidgets.length > 0) {
        this.proofWidgetsTab = `proof_widget_${this.proofWidgets[0].id}`;
      } else {
        this.proofWidgetsTab = '';
      }

      this.$loader.end('openTrackingModal');

      this.trackingFlow = 'Tracking Link';
      this.selectedCheckoutFlow = 0;
      this.$root.$emit('modal-open', 'tracking-modal');
    },

    setLocalProperties() {
      this.selectedCheckoutData = [];
      this.model = {
        isEdit: false,
        checkout_links: [],
        name: '',
        skip_field_arr: [],
        force_affiliate: false,
        force_affiliate_id: null,
        ask_for_password_select: 0,
      };
      this.currentTab = 'ConfigStep';
      this.selectedCheckoutStepType = 1;
    },

    async openCheckoutPageLink(id) {
      this.$loader.start('openCheckoutPageLink');
      this.selectedProduct = await Product.$find(this.$route.params.product_id);
      this.multiPricePointLink = await this.selectedProduct.checkoutLinks().$find(id);
      this.checkout_link = this.multiPricePointLink.checkout_link;
      this.checkout_embed = this.multiPricePointLink.checkout_embed;
      this.showCheckoutLinkModal = true;
      this.$loader.end('openCheckoutPageLink');
    },

    async cloneCheckout(checkoutLink) {
      this.openCheckoutMenuId = null;
      this.$loader.start('cloneCheckout');
      try {
        await axios.post('clone-checkout-link', { id: checkoutLink.id });
        this.checkoutLinks = await this.selectedProduct.checkoutLinks().$get();
        this.alertProps = { show: true, variant: 'success', title: 'Checkout cloned', message: `Created a copy of “${this.checkoutDisplayName(checkoutLink)}”.` };
      } catch (error) {
        this.alertProps = { show: true, variant: 'error', title: 'Unable to clone checkout', message: 'The checkout could not be cloned. Please try again.' };
      } finally {
        this.$loader.end('cloneCheckout');
      }
    },

    openDeleteCheckoutModal(checkoutLink) {
      this.checkoutLinkToDelete = checkoutLink;
      this.openCheckoutMenuId = null;
      this.showDeleteCheckoutModal = true;
    },

    toggleCheckoutMenu(id) {
      this.openCheckoutMenuId = this.openCheckoutMenuId === id ? null : id;
    },

    async deleteCheckout() {
      if (!this.checkoutLinkToDelete) return;
      const checkoutLink = this.checkoutLinkToDelete;
      this.$loader.start('deleteCheckout');
      try {
        const checkoutData = await CheckoutLink.$find(checkoutLink.id);
        await checkoutData.delete();
        this.checkoutLinks = this.checkoutLinks.filter((item) => item.id !== checkoutLink.id);
        this.alertProps = { show: true, variant: 'success', title: 'Checkout deleted', message: `“${this.checkoutDisplayName(checkoutLink)}” was deleted.` };
      } catch (error) {
        this.alertProps = { show: true, variant: 'error', title: 'Unable to delete checkout', message: 'The checkout could not be deleted. No changes were made.' };
      } finally {
        this.checkoutLinkToDelete = null;
        this.showDeleteCheckoutModal = false;
        this.$loader.end('deleteCheckout');
      }
    },



    async setPricePointEnabledOnMultiCheckout(value, linkid, pricingPointId) {
      this.$loader.start('setPricePointEnabledOnMultiCheckout');
      const selectedCheckoutLink = await this.selectedProduct.checkoutLinks().$find(linkid);
      const objIndex = selectedCheckoutLink.pricing_options.findIndex(
        (obj) => obj.id === pricingPointId
      );
      selectedCheckoutLink.pricing_options[objIndex].enabled = value;
      await selectedCheckoutLink.save();
      this.$loader.end('setPricePointEnabledOnMultiCheckout');
    },

    async generateMultiCheckoutLink() {
      const enabledPricePoints = this.selectedProductPricePoints.filter(
        (pricePoint) => {
          return pricePoint.enabled;
        }
      );

      if (enabledPricePoints.length > 0) {
        this.$loader.start('generateMultiCheckoutLink');
        const enabledPricingOptionsArray = [];

        for (let i = 0; i < enabledPricePoints.length; i += 1) {
          const singleEnabledPricingOption = {
            id: enabledPricePoints[i].id,
            enabled: true,
          };
          enabledPricingOptionsArray.push(singleEnabledPricingOption);
        }

        const newMultiPricePointLink = new CheckoutLink({
          product_id: this.selectedProduct.id,
          price_point_id: 0,
          pricing_options: enabledPricingOptionsArray,
          is_onestep: this.selectedCheckoutStepType,
        });

        const savedCheckoutLinkData = await newMultiPricePointLink.save();
        this.multiPricePointLink = await this.selectedProduct.checkoutLinks().$find(savedCheckoutLinkData.data.id);
        this.$loader.end('generateMultiCheckoutLink');
      } else {
        this.alertProps = {
          show: true,
          variant: 'error',
          title: this.$t('frontend_error'),
          message: this.$t('frontend_groovesell_swal_select_atleast_one_payment_option'),
        };
      }
    },

    goToMultiCheckoutLinks() {
      this.multiPricePointLink = null;
    },

    checkoutlinksNavigate(navigateMethod, index) {
      this.$refs.checkoutLinksWizard.changeTab(this.checkoutLinksIndex, index);
    },



    onCopyProofWidget() {
      Notify.success(this.$t('frontend_proof_widget_code_copy_to_clipboard'));
    },

    onErrorProofWidget() {
      Notify.fail(this.$t('frontend_unable_copy_proof_widget_code'));
    },
  },
};
</script>
<style>
.gs-checkout-v2 {
  --checkout-ink: #171b3f;
  --checkout-copy: #697189;
  --checkout-muted: #8f96ad;
  --checkout-border: #e6e9f2;
  --checkout-surface: #ffffff;
  --checkout-soft: #f7f8fc;
  --checkout-accent: var(--gs-primary, #f22987);
  --checkout-active: #4c91f7;
  --checkout-active-soft: #edf7ff;
  --checkout-active-border: #b9d8ff;
  min-height: calc(100vh - 80px);
  padding: 18px 24px 54px;
  background: #f4f5fb;
  color: var(--checkout-ink);
}

.gs-checkout-v2__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 14px;
  padding: 18px 20px;
  background: var(--checkout-surface);
  border: 1px solid #edf0f6;
  border-radius: 8px;
  box-shadow: 0 14px 32px rgba(31, 37, 72, .06);
}

.gs-checkout-v2__summary-copy {
  min-width: 0;
}

.gs-checkout-v2__back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 11px;
  padding: 0;
  color: #747c94;
  font-size: 12px;
  font-weight: 700;
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: color .15s ease;
}

.gs-checkout-v2__back:hover,
.gs-checkout-v2__back:focus-visible {
  color: var(--checkout-accent);
}

.gs-checkout-v2__back svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.gs-checkout-v2__eyebrow {
  margin: 0 0 4px !important;
  color: var(--checkout-accent) !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-checkout-v2__summary h1 {
  margin: 0;
  color: var(--checkout-ink);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0 !important;
}

.gs-checkout-v2__summary-copy > p:last-child {
  max-width: 670px;
  margin: 7px 0 0;
  color: var(--checkout-copy);
  font-size: 14px;
  line-height: 1.5;
}

.gs-checkout-v2__summary-side {
  display: grid;
  flex: 0 0 auto;
  justify-items: end;
  gap: 14px;
}

.gs-checkout-v2__stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.gs-checkout-v2__stats span {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-height: 32px;
  padding: 5px 9px;
  color: #7c849c;
  font-size: 12px;
  background: var(--checkout-soft);
  border: 1px solid #edf0f6;
  border-radius: 6px;
}

.gs-checkout-v2__stats strong {
  color: var(--checkout-ink);
  font-size: 14px;
}

.gs-checkout-v2__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gs-checkout-v2__link {
  padding: 0;
  color: var(--checkout-accent);
  font-size: 13px;
  font-weight: 700;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-checkout-v2__plus {
  margin-right: 7px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
}

.gs-checkout-v2__toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
  padding: 15px 18px 17px;
  background: var(--checkout-surface);
  border: 1px solid var(--checkout-border);
  border-radius: 8px;
}

.gs-checkout-v2__toolbar > div {
  flex: 1 1 auto;
  min-width: 0;
}

.gs-checkout-v2__toolbar-label,
.gs-checkout-v2__section-heading p,
.gs-checkout-v2__state-eyebrow {
  margin: 0 0 8px;
  color: var(--checkout-muted);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-checkout-v2__toolbar-help {
  max-width: 260px;
  margin: 0 0 2px;
  color: #8a91a7;
  font-size: 12px;
  line-height: 1.45;
  text-align: right;
}

.gs-checkout-v2__section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin: 0 2px 12px;
}

.gs-checkout-v2__section-heading p {
  margin-bottom: 3px;
}

.gs-checkout-v2__section-heading h2 {
  margin: 0;
  color: var(--checkout-ink);
  font-size: 18px;
  font-weight: 700;
}

.gs-checkout-v2__section-heading > span {
  color: #8a91a7;
  font-size: 12px;
  font-weight: 700;
}

.gs-checkout-v2__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 16px;
}

.gs-checkout-v2__card,
.gs-checkout-v2__state {
  background: var(--checkout-surface);
  border: 1px solid var(--checkout-border);
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(31, 37, 72, .045);
}

.gs-checkout-v2__card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  min-height: 238px;
  padding: 18px;
  overflow: hidden;
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease, background .15s ease;
}

.gs-checkout-v2__card:hover {
  border-color: var(--checkout-active-border);
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  box-shadow: 0 16px 34px rgba(53, 112, 188, .11);
  transform: translateY(-1px);
}

.gs-checkout-v2__card-main {
  display: block;
  width: 100%;
  min-width: 0;
}

.gs-checkout-v2__card-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin-bottom: 14px;
}

.gs-checkout-v2__card-icon {
  display: grid;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  color: var(--checkout-active);
  background: var(--checkout-active-soft);
  border: 1px solid var(--checkout-active-border);
  border-radius: 8px;
  place-items: center;
}

.gs-checkout-v2__card-copy {
  width: 100%;
  min-width: 0;
}

.gs-checkout-v2__card h2 {
  margin: 0;
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  color: var(--checkout-ink);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  letter-spacing: 0 !important;
  overflow-wrap: anywhere;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.gs-checkout-v2__card-kicker {
  margin: 0 0 5px !important;
  color: var(--checkout-muted) !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-checkout-v2__card-topline span {
  padding: 4px 8px;
  color: #2874d0;
  font-size: 11px;
  font-weight: 800;
  border-radius: 999px;
  background: var(--checkout-active-soft);
  border: 1px solid #d9ebff;
}

.gs-checkout-v2__card-copy > p {
  margin: 7px 0 0;
  color: #7c849c;
  font-size: 13px;
  line-height: 1.45;
}

.gs-checkout-v2__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
  margin: 16px 0 0;
  padding: 14px;
  background: #f8faff;
  border: 1px solid #edf2fa;
  border-radius: 8px;
}

.gs-checkout-v2__meta div {
  min-width: 0;
}

.gs-checkout-v2__meta dt {
  margin: 0 0 4px;
  color: #8a91a7;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.gs-checkout-v2__meta dd {
  margin: 0;
  overflow: hidden;
  color: var(--checkout-ink);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-checkout-v2__card-actions {
  display: flex;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: auto;
  gap: 8px;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity .15s ease, transform .15s ease;
}

.gs-checkout-v2__card:hover .gs-checkout-v2__card-actions,
.gs-checkout-v2__card:focus-within .gs-checkout-v2__card-actions {
  opacity: 1;
  transform: translateY(0);
}

.gs-checkout-v2__card-actions > .button,
.gs-checkout-v2__card-actions > button {
  flex: 1 1 132px;
  justify-content: center;
}

.gs-checkout-v2__button-icon {
  display: inline-flex;
  margin-right: 6px;
}

.gs-checkout-v2__state {
  grid-column: 1 / -1;
  padding: 48px 28px;
  text-align: center;
}

.gs-checkout-v2__state-icon {
  display: grid;
  width: 54px;
  height: 54px;
  margin: 0 auto 16px;
  color: #db395d;
  background: #fff0f4;
  border: 1px solid #ffd9e2;
  border-radius: 14px;
  place-items: center;
}

.gs-checkout-v2__state-icon svg {
  width: 26px;
  height: 26px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.gs-checkout-v2__state-icon--empty {
  color: var(--checkout-accent);
}

.gs-checkout-v2__state-eyebrow {
  margin-bottom: 5px;
  color: var(--checkout-accent);
}

.gs-checkout-v2__state h2 {
  margin: 0;
  color: var(--checkout-ink);
  font-size: 22px;
  font-weight: 700;
}

.gs-checkout-v2__state > p:not(.gs-checkout-v2__state-eyebrow) {
  max-width: 560px;
  margin: 9px auto 0;
  color: var(--checkout-copy);
  font-size: 14px;
  line-height: 1.55;
}

.gs-checkout-v2__state-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 22px;
}

.gs-checkout-v2__state > small {
  display: block;
  margin-top: 15px;
  color: #969db1;
  font-size: 11px;
}

.gs-checkout-v2__empty > button,
.gs-checkout-v2__empty > .button,
.gs-checkout-v2__empty > *:last-child {
  margin-top: 22px;
}

.gs-checkout-builder-modal .gs-modal__panel {
  width: min(calc(100vw - 64px), 1680px);
  height: calc(100vh - 64px);
  max-height: 980px;
}

.gs-checkout-builder-modal .gs-modal__body {
  min-height: 0;
  overflow: visible;
}

.gs-checkout-wizard {
  display: grid;
  height: 100%;
  grid-template-columns: 250px minmax(0, 1fr);
  min-height: 0;
  overflow: visible;
  background: var(--gs-background, #f7f8fb);
}

.gs-checkout-wizard__content {
  min-width: 0;
  min-height: 0;
  padding: 28px 32px 40px;
  overflow: auto;
  overscroll-behavior: contain;
  background:
    radial-gradient(circle at 88% 8%, rgba(37, 99, 235, .06), transparent 32%),
    var(--gs-background, #f7f8fb);
  scrollbar-gutter: stable;
}

.gs-checkout-builder-modal .color-picker,
.gs-checkout-builder-modal [class*="color-picker"],
.gs-checkout-builder-modal .vc-sketch,
.gs-checkout-builder-modal .vc-chrome,
.gs-checkout-builder-modal .vc-picker {
  position: relative;
  z-index: 100000 !important;
}

.gs-checkout-v2__card-topline { position: relative; }
.gs-checkout-v2__card-menu { position: relative; margin-left: auto; }
.gs-checkout-v2__menu-trigger {
  width: 30px;
  height: 30px;
  color: #667085;
  font-size: 22px;
  line-height: 1;
  background: transparent;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
}
.gs-checkout-v2__menu-trigger:hover { background: #f2f4f7; color: #182230; }
.gs-checkout-v2__menu {
  position: absolute;
  top: 34px;
  right: 0;
  z-index: 20;
  display: grid;
  min-width: 130px;
  padding: 5px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(29, 45, 77, .16);
}
.gs-checkout-v2__menu button { padding: 9px 10px; color: #182230; text-align: left; background: transparent; border: 0; border-radius: 6px; cursor: pointer; }
.gs-checkout-v2__menu button:hover { background: #f8fafc; }
.gs-checkout-v2__menu button.is-danger { color: #b42318; }
.gs-checkout-v2__delete-modal { padding: 4px 0 8px; text-align: center; }
.gs-checkout-v2__delete-modal-icon { display: grid; width: 42px; height: 42px; margin: 0 auto 10px; color: #b42318; font-weight: 800; background: #fef3f2; border-radius: 50%; place-items: center; }
.gs-checkout-v2__delete-modal h3 { margin: 0; color: #182230; font-size: 17px; }
.gs-checkout-v2__delete-modal p { margin: 10px auto 0; max-width: 390px; color: #667085; font-size: 13px; line-height: 1.5; }
.gs-checkout-v2__delete-modal-actions { display: flex; justify-content: center; gap: 10px; margin-top: 22px; }
.gs-checkout-v2__delete-confirm { background: #b42318 !important; border-color: #b42318 !important; }

.gs-checkout-wizard__content-inner {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
}

.gs-checkout-wizard__content-inner.is-links-step {
  min-height: 100%;
}

.gs-checkout-wizard__step-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
}

.gs-checkout-wizard__step-header p {
  margin: 0 0 6px;
  color: var(--gs-active, #2563eb);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .09em;
  text-transform: uppercase;
}

.gs-checkout-wizard__step-header h2 {
  margin: 0;
  color: var(--gs-foreground, #182230);
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -.02em;
  line-height: 1.2;
}

.gs-checkout-wizard__step-header div > span {
  display: block;
  margin-top: 7px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 13px;
  line-height: 1.5;
}

.gs-checkout-wizard__save-note {
  flex: 0 0 auto;
  padding: 7px 10px;
  color: #527091;
  font-size: 11px;
  font-weight: 700;
  background: #edf6ff;
  border: 1px solid #d2e8ff;
  border-radius: 999px;
}

.gs-checkout-wizard__step-surface {
  min-height: 360px;
  padding: 24px;
  background: var(--gs-surface, #fff);
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(29, 45, 77, .06);
}

.is-links-step .gs-checkout-wizard__step-surface {
  min-height: 480px;
}

@media (max-width: 1100px) {
  .gs-checkout-v2__summary {
    align-items: flex-start;
  }

  .gs-checkout-v2__summary-side {
    max-width: 390px;
  }

  .gs-checkout-v2__grid {
    grid-template-columns: 1fr;
  }

  .gs-checkout-wizard {
    grid-template-columns: 220px minmax(0, 1fr);
  }

  .gs-checkout-wizard__content {
    padding-right: 24px;
    padding-left: 24px;
  }
}

@media (max-width: 780px) {
  .gs-checkout-v2 {
    padding: 14px 12px 36px;
  }

  .gs-checkout-v2__summary,
  .gs-checkout-v2__toolbar,
  .gs-checkout-v2__card {
    flex-direction: column;
    align-items: flex-start;
  }

  .gs-checkout-v2__summary,
  .gs-checkout-v2__toolbar {
    padding: 16px;
  }

  .gs-checkout-v2__summary-side,
  .gs-checkout-v2__stats,
  .gs-checkout-v2__header-actions,
  .gs-checkout-v2__card-main,
  .gs-checkout-v2__card-actions {
    width: 100%;
  }

  .gs-checkout-v2__summary-side {
    justify-items: stretch;
    max-width: none;
  }

  .gs-checkout-v2__stats {
    justify-content: flex-start;
  }

  .gs-checkout-v2__header-actions {
    justify-content: space-between;
  }

  .gs-checkout-v2__toolbar-help {
    max-width: none;
    text-align: left;
  }

  .gs-checkout-v2__card-actions {
    justify-content: flex-start;
    padding-left: 0;
    opacity: 1;
    transform: none;
  }

  .gs-checkout-v2__state {
    padding: 38px 20px;
  }

  .gs-checkout-v2__grid {
    grid-template-columns: 1fr;
  }

  .gs-checkout-wizard {
    display: block;
  }

  .gs-checkout-wizard__content {
    padding: 18px 14px 36px;
  }

  .gs-checkout-builder-modal .gs-modal__panel {
    width: calc(100vw - 24px);
    height: calc(100vh - 24px);
  }

  .gs-checkout-wizard__step-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .gs-checkout-wizard__step-surface {
    padding: 18px;
  }
}

@media (max-width: 520px) {
  .gs-checkout-v2__header-actions,
  .gs-checkout-v2__state-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .gs-checkout-v2__card-actions {
    padding-left: 0;
  }

  .gs-checkout-v2__card-actions > * {
    flex: 1 1 auto;
  }
}
</style>
@import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Amatic+SC:wght@400;700&family=Architects+Daughter&family=Asap:wght@400;700&family=Balsamiq+Sans:wght@400;700&family=Barlow:wght@400;700;900&family=Bebas+Neue&family=Bitter:wght@400;700;900&family=Cabin:wght@400;700&family=Cairo:wght@400;700&family=Cormorant+Garamond:wght@400;700&family=Crimson+Text:wght@400;700&family=Dancing+Script:wght@400;700&family=Fira+Sans:wght@400;700;900&family=Fjalla+One&family=Indie+Flower&family=Josefin+Sans:wght@400;700&family=Lato:wght@400;700;900&family=Libre+Baskerville:wght@400;700&family=Libre+Franklin:wght@400;700;900&family=Lobster&family=Lora:wght@400;700&family=Martel:wght@400;700;900&family=Merriweather:wght@400;700;900&family=Montserrat:wght@400;700;900&family=Mukta:wght@400;700&family=Noto+Sans+JP:wght@400;700&family=Noto+Sans+KR:wght@400;700;900&family=Noto+Sans:wght@400;700&family=Noto+Serif:wght@400;700&family=Nunito+Sans:wght@200;300;400;700;900&family=Nunito:wght@300;400;700;900&family=Old+Standard+TT:wght@400;700&family=Open+Sans+Condensed:wght@300;700&family=Open+Sans:wght@300;400;700&family=Oswald:wght@400;700&family=Overpass:wght@400;700;900&family=Oxygen:wght@300;400;700&family=PT+Sans+Narrow:wght@400;700&family=PT+Sans:wght@400;700&family=PT+Serif:wght@400;700&family=Pacifico&family=Playfair+Display:wght@400;700;900&family=Poppins:ital,wght@0,400;0,700;1,900&family=Raleway:wght@400;700;900&family=Roboto+Condensed:wght@400;700&family=Roboto+Slab:wght@400;700;900&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&family=Rubik:ital,wght@0,400;0,700;1,900&family=Shadows+Into+Light&family=Signika:wght@400;700&family=Slabo+27px&family=Source+Code+Pro:wght@400;700;900&family=Source+Sans+Pro:wght@400;700;900&family=Source+Serif+Pro:wght@400;700;900&family=Tajawal:wght@400;700;900&family=Titillium+Web:wght@400;700;900&family=Ubuntu:wght@400;700&family=Work+Sans:wght@400;700;900&display=swap');
